import { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { Outlet } from 'react-router-dom';
import {
  DEFAULT_VALUES,
  useRegistrationForm,
  type RegistrationFormValues,
} from '@/hooks/useRegistrationForm';
import { useWizardSessionId } from '@/hooks/useWizardSessionId';
import { WizardSessionIdProvider } from '@/hooks/WizardSessionIdContext';
import { WizardSessionDebugLine } from '@/components/WizardSessionDebugLine';
import { DraftAutosaveLine } from '@/components/DraftAutosaveLine';
import { CorrectionIssuesBanner } from '@/components/CorrectionIssuesBanner';
import { DraftAutosaveProvider } from '@/hooks/useDraftAutosave';
import { WizardIssuesProvider } from '@/hooks/WizardIssuesContext';
import { useAreaSession } from '@/hooks/AreaSessionContext';
import { consumeResumeDraftStash } from '@/lib/resumeDraftStash';
import { hydrateDraftOverDefaults } from '@/lib/draftHydration';
import type { AreaOpenIssue } from '@/lib/areaApi';

/**
 * RegistrationLayout — owns the react-hook-form instance shared across the
 * four wizard pages (Organizzazione → Admin → Sede → Riepilogo).
 *
 * Each child page reaches into the form via `useFormContext()`. The form
 * instance is created once when the layout mounts and survives all
 * sibling-route navigations underneath. A hard refresh on any wizard
 * route resets the form to defaults — that is the intended behaviour
 * (no value persistence across reloads).
 *
 * The post-submit phases (`/documenti`, `/success`) live as siblings
 * under the same path prefix; they don't read form context but stay
 * inside this tree so the URL hierarchy is uniform.
 *
 * Wizard session ID — `useWizardSessionId` resolves a stable per-tab
 * UUID, syncs it to the URL as `?sid=<uuid>` and persists it in
 * sessionStorage. Surfaced to children via context so every BE call
 * can pass it for log correlation. See `useWizardSessionId.ts`.
 *
 * PR-ONB-PRECHECK-3 — the autosave machine is hoisted here
 * (`DraftAutosaveProvider`, single instance) so the status line AND the
 * SummaryPage area submit share the same state; the resume stash may carry
 * the OPEN correction issues, surfaced per-step via `WizardIssuesProvider`.
 */
export function RegistrationLayout(): JSX.Element {
  const form = useRegistrationForm();
  const wizardSessionId = useWizardSessionId();
  const session = useAreaSession();
  const hydratedRef = useRef(false);
  const [issues, setIssues] = useState<readonly AreaOpenIssue[]>([]);

  // PR-ONB-RESUME-1B — resume hydration: the area "Dati" page stashes the
  // SERVER draft (sessionStorage, short TTL) and navigates here; on mount we
  // hydrate the form (deep-merged over defaults so every controlled input
  // keeps a value) and mark the session bound so autosave continues against
  // the right revision. One-shot per layout mount.
  useEffect(() => {
    if (hydratedRef.current || !session.enabled) return;
    hydratedRef.current = true;
    const stash = consumeResumeDraftStash();
    if (!stash) return;
    form.reset(
      hydrateDraftOverDefaults(
        DEFAULT_VALUES as unknown as Record<string, unknown>,
        stash.draftPayload,
      ) as unknown as RegistrationFormValues,
    );
    session.markBound({
      requestPublicId: stash.requestPublicId,
      draftRevision: stash.draftRevision,
    });
    if (stash.openIssues && stash.openIssues.length > 0) {
      setIssues(stash.openIssues as AreaOpenIssue[]);
    }
  }, [form, session]);

  return (
    <FormProvider {...form}>
      <WizardSessionIdProvider value={wizardSessionId}>
        <WizardIssuesProvider value={issues}>
          <DraftAutosaveProvider>
            <Outlet />
            {/* PR-ONB-PRECHECK-3 — per-step corrections asked by the BO. */}
            <CorrectionIssuesBanner />
            {/* PR-ONB-RESUME-1B — autosave status + "Salva ed esci". Renders
                nothing for anonymous/legacy sessions. */}
            <DraftAutosaveLine />
            {/*
              Debug-only: surfaces the wizard session id below every step's
              footer (incl. the "Continua" button) so testers can quote a
              stable reference. Drop this line when the testing phase ends.
            */}
            <WizardSessionDebugLine />
          </DraftAutosaveProvider>
        </WizardIssuesProvider>
      </WizardSessionIdProvider>
    </FormProvider>
  );
}
